import React, { useEffect, useMemo } from 'react';
import Carousel from 'react-multi-carousel';
import { useDispatch, useSelector } from 'react-redux';
import {
  CAROUSEL_V2_BREAKPOINT,
  IMAGE_CONSTANT,
  LOCAL_STORAGE_CONSTANT,
} from '../../constants/app';
import LazyImage from '../../ui-core/Image';
import { SEGMENT_EVENTS, trackEvent } from '../../utils/Segment';
import { encodeURLParams } from '../../utils/url';
import { APP_ROUTES, getRoute } from '../../route';
import './style.css';
import { extractJSON } from '../../services/jsonUtil';
import {
  setOtherSelectedFilters,
  setSelectedFilters,
} from '../../store/action/common';
import {
  doPlansRequest,
  doAllPlansRequest,
  doOtherPlansRequest,
} from '../../store/action/plan';
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min';

export default function YourRecommendedPlan() {
  const dispatch = useDispatch();
  const history = useHistory();
  const completeOnboarding = useMemo(
    () => JSON.parse(localStorage.getItem('completeOnboarding')),
    [],
  );
  const plans = useSelector(state => state.plan.plans); // get primary pain point
  const otherPlans = useSelector(state => state.plan.otherPlans); // get additional pain point
  const allPlans = useSelector(state => state.plan.allPlans); // all plans without filters

  // Create a combined array of up to 3 plans for the "Other Plans" section
  const displayPlans = useMemo(() => {
    const primaryPlanId = plans?.data?.[0]?.id;
    const otherPlansData = (otherPlans?.data || []).filter(
      p => p.id !== primaryPlanId,
    );
    const allPlansData = (allPlans?.data || []).filter(
      p => p.id !== primaryPlanId,
    );

    // Start with otherPlans
    let combined = [...otherPlansData];

    // If we need more plans to reach 3, add from allPlans (avoiding duplicates)
    if (combined.length < 3) {
      const otherPlansIds = new Set(otherPlansData.map(plan => plan.id));
      const remainingFromAllPlans = allPlansData.filter(
        plan => !otherPlansIds.has(plan.id),
      );

      const needed = 3 - combined.length;
      combined = [...combined, ...remainingFromAllPlans.slice(0, needed)];
    }

    // Ensure we only return up to 3 plans
    return combined.slice(0, 3);
  }, [otherPlans, allPlans, plans]);

  // if there were no completeOnboarding, send user to home
  useEffect(() => {
    if (!completeOnboarding) {
      history.push(APP_ROUTES.HOME);
    }
  }, [completeOnboarding, history]);

  useEffect(() => {
    const onboardingObject = extractJSON(
      LOCAL_STORAGE_CONSTANT.COMPLETE_ONBOARDING,
    );
    const bodyParts =
      onboardingObject?.user_profile_attributes?.primary_point ?? [];
    const target_point =
      onboardingObject?.user_profile_attributes?.target_point ?? [];
    dispatch(setSelectedFilters({ selectedFilters: bodyParts }));
    dispatch(setOtherSelectedFilters({ otherSelectedFilters: target_point }));
  }, [completeOnboarding, dispatch]);

  useEffect(() => {
    dispatch(doPlansRequest());
    dispatch(doOtherPlansRequest());
    dispatch(doAllPlansRequest()); // all plans without filters
  }, []);

  const handlePlanStartNow = plan => {
    if (plan) {
      trackEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
        page: 'Your Recommended Plan',
        section_title: 'Other Plan',
        button_title: 'Start Now',
      });
      const { card_color, title, plan_type } = plan.attributes || {};
      const params = {
        card_color,
        plan_type,
        title,
      };
      const options = {
        pathParam: {
          id: plan.id,
        },
        searchParam: {
          params: encodeURLParams(params),
        },
      };
      const route = getRoute(APP_ROUTES.HOME_SUB_SECTION, options);
      history.push(route);
    }
  };

  const handleStartNow = () => {
    const plan = plans.data?.[0];
    if (plan) {
      trackEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
        page: 'Your Recommended Plan',
        section_title: 'Main Plan',
        button_title: 'Start Now',
      });
      const { card_color, title, plan_type } = plan.attributes || {};
      const params = {
        card_color,
        plan_type,
        title,
      };
      const options = {
        pathParam: {
          id: plan.id,
        },
        searchParam: {
          params: encodeURLParams(params),
        },
      };
      const route = getRoute(APP_ROUTES.HOME_SUB_SECTION, options);
      history.push(route);
    }
  };

  const handleHomeClick = () => {
    const option = {};
    const route = getRoute(APP_ROUTES.HOME, option);
    history.push(route);
  };

  const handleViewAllPlans = () => {
    trackEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
      page: 'Your Recommended Plan',
      section_title: 'Other Plans',
      button_title: 'View All Plans',
    });
    history.push(APP_ROUTES.HOME);
  };

  return (
    <div className="your-recommended-plan">
      <Link to={APP_ROUTES.HOME}>
        <button type="button" className="close-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.854 12.1465C12.9005 12.193 12.9373 12.2481 12.9625 12.3088C12.9876 12.3695 13.0006 12.4346 13.0006 12.5003C13.0006 12.566 12.9876 12.631 12.9625 12.6917C12.9373 12.7524 12.9005 12.8076 12.854 12.854C12.8076 12.9005 12.7524 12.9373 12.6917 12.9625C12.631 12.9876 12.566 13.0006 12.5003 13.0006C12.4346 13.0006 12.3695 12.9876 12.3088 12.9625C12.2481 12.9373 12.193 12.9005 12.1465 12.854L8.00028 8.70715L3.85403 12.854C3.76021 12.9478 3.63296 13.0006 3.50028 13.0006C3.3676 13.0006 3.24035 12.9478 3.14653 12.854C3.05271 12.7602 3 12.633 3 12.5003C3 12.3676 3.05271 12.2403 3.14653 12.1465L7.2934 8.00028L3.14653 3.85403C3.05271 3.76021 3 3.63296 3 3.50028C3 3.3676 3.05271 3.24035 3.14653 3.14653C3.24035 3.05271 3.3676 3 3.50028 3C3.63296 3 3.76021 3.05271 3.85403 3.14653L8.00028 7.2934L12.1465 3.14653C12.2403 3.05271 12.3676 3 12.5003 3C12.633 3 12.7602 3.05271 12.854 3.14653C12.9478 3.24035 13.0006 3.3676 13.0006 3.50028C13.0006 3.63296 12.9478 3.76021 12.854 3.85403L8.70715 8.00028L12.854 12.1465Z"
              fill="white"
            />
          </svg>
        </button>
      </Link>

      <div className="top-section">
        <div className="container-fluid">
          <header className="text-white">
            <h2
              className="logo-text mb-0"
              onClick={handleHomeClick}
              style={{ cursor: 'pointer' }}
            >
              Jen
            </h2>
          </header>
        </div>
        <div className="container">
          <div className="section-big-title text-white">
            Your Recommended Plan
          </div>
          <div className="main-plan">
            <div className="image-container">
              <span className="duration-badge">
                {' '}
                {plans.data?.[0]?.attributes?.duration}
              </span>
              <img
                src={plans.data?.[0]?.attributes?.thumbnail}
                alt={plans.data?.[0]?.attributes?.title}
              />
            </div>
            <div className="info-container">
              <div className="main-plan-title">
                {plans.data?.[0]?.attributes?.title}
              </div>
              <div className="best-for">
                <b>Best For :</b>{' '}
                {plans.data?.[0]?.attributes?.issues
                  .map(issue => issue)
                  .join(', ')}
              </div>
              {plans.data?.[0]?.attributes?.equipments?.length > 0 && (
                <div className="best-for">
                  <b>Equipment :</b>{' '}
                  {plans.data?.[0]?.attributes?.equipments
                    .map(equipment => equipment)
                    .join(', ')}
                </div>
              )}
              <button type="button" onClick={handleStartNow}>
                Start Now
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="other-plans">
        <div className="container">
          <div className="section-big-title">Other Plans</div>
          <Carousel
            responsive={CAROUSEL_V2_BREAKPOINT}
            showDots={false}
            containerClass=""
            swipeable
            draggable
          >
            {displayPlans?.map(item => {
              return (
                <div key={item?.id} className="other-plan-card">
                  <div>
                    <div className="other-image-container">
                      <span className="duration-badge">
                        {' '}
                        {item.attributes?.duration}
                      </span>
                      <LazyImage
                        src={item?.attributes?.thumbnail}
                        className="img-fluid"
                        placeholderSrc={IMAGE_CONSTANT.placeholderImage}
                        errorSrc={IMAGE_CONSTANT.placeholderImage}
                      />
                    </div>

                    <div className="content">
                      <div className="main-plan-title">
                        {item.attributes?.title}
                      </div>
                      <div className="best-for">
                        <b>Best For :</b>{' '}
                        {item.attributes?.issues.map(issue => issue).join(', ')}
                      </div>
                      {item.attributes?.equipments?.length > 0 && (
                        <div className="best-for">
                          <b>Equipment :</b>{' '}
                          {item.attributes?.equipments
                            .map(equipment => equipment)
                            .join(', ')}
                        </div>
                      )}
                      <div className="flex-grow"></div>
                      <button
                        className="start-now-btn"
                        onClick={() => handlePlanStartNow(item)}
                      >
                        Start Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </Carousel>
          <div className="links-parent">
            <button
              type="button"
              className="continue-btn"
              onClick={handleViewAllPlans}
            >
              Choose Plan Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
